import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Link,
  IconButton,
  Tab,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';

const CompanyHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0052CC 0%, #0747A6 100%)',
  padding: theme.spacing(6, 0),
  color: 'white',
  marginBottom: theme.spacing(4),
}));

const CompanyLogo = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: theme.spacing(2),
  backgroundColor: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const mockCompany = {
  name: 'Tech Corp',
  logo: 'TC',
  description: `Tech Corp is a leading technology company specializing in innovative solutions for businesses worldwide. We are committed to creating cutting-edge products and services that transform industries and improve people's lives.

Our mission is to empower businesses through technology, and we're looking for talented individuals who share our vision and passion for innovation.`,
  location: 'New York, NY',
  industry: 'Technology',
  size: '100-500 employees',
  founded: '2015',
  website: 'www.techcorp.com',
  social: {
    linkedin: 'techcorp',
    twitter: '@techcorp',
  },
  benefits: [
    'Competitive salary',
    'Health insurance',
    '401(k) matching',
    'Flexible work hours',
    'Remote work options',
    'Professional development',
    'Team events',
  ],
  jobs: [
    {
      id: 1,
      title: 'Senior Frontend Developer',
      location: 'New York, NY',
      type: 'Full-time',
      salary: '$80k-$120k',
      posted: '2 days ago',
      description: 'We are looking for an experienced Frontend Developer...',
      tags: ['React', 'TypeScript', 'Node.js'],
    },
    {
      id: 2,
      title: 'Product Manager',
      location: 'Remote',
      type: 'Full-time',
      salary: '$90k-$130k',
      posted: '1 week ago',
      description: 'Join our product team to drive innovation...',
      tags: ['Product', 'Agile', 'UX'],
    },
    {
      id: 3,
      title: 'Backend Developer',
      location: 'New York, NY',
      type: 'Full-time',
      salary: '$85k-$125k',
      posted: '2 weeks ago',
      description: 'Looking for a skilled Backend Developer...',
      tags: ['Python', 'Django', 'PostgreSQL'],
    },
  ],
};

function CompanyViewPage() {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <CompanyHeader>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={3}>
              <CompanyLogo>
                <Typography variant="h4" color="primary">
                  {mockCompany.logo}
                </Typography>
              </CompanyLogo>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography variant="h4" gutterBottom>
                {mockCompany.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="body1">
                  <LocationOnIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockCompany.location}
                </Typography>
                <Typography variant="body1">
                  <WorkIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockCompany.industry}
                </Typography>
                <Typography variant="body1">
                  <PeopleIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockCompany.size}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'white' } }}
                >
                  Follow Company
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white' } }}
                >
                  View All Jobs
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </CompanyHeader>

      <Container>
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  About Us
                </Typography>
                <Typography variant="body1" paragraph>
                  {mockCompany.description}
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Benefits & Perks
                </Typography>
                <Grid container spacing={2}>
                  {mockCompany.benefits.map((benefit) => (
                    <Grid item xs={12} sm={6} key={benefit}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={benefit}
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Open Positions" />
                    <Tab label="Company Culture" />
                    <Tab label="Reviews" />
                  </Tabs>
                </Box>

                {tabValue === 0 && (
                  <Box>
                    {mockCompany.jobs.map((job) => (
                      <Card key={job.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {job.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                              <LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              {job.location}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              <WorkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              {job.type}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              <AttachMoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              {job.salary}
                            </Typography>
                          </Box>
                          <Typography variant="body2" paragraph>
                            {job.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            {job.tags.map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Box>
                          <Button variant="outlined" color="primary">
                            Apply Now
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                {tabValue === 1 && (
                  <Typography variant="body1">
                    Company culture content will be displayed here...
                  </Typography>
                )}

                {tabValue === 2 && (
                  <Typography variant="body1">
                    Company reviews will be displayed here...
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Company Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <BusinessIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Industry"
                      secondary={mockCompany.industry}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Company Size"
                      secondary={mockCompany.size}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AccessTimeIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Founded"
                      secondary={mockCompany.founded}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LanguageIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Website"
                      secondary={
                        <Link href={`https://${mockCompany.website}`} target="_blank" rel="noopener">
                          {mockCompany.website}
                        </Link>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Social Media
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <IconButton
                    href={`https://linkedin.com/company/${mockCompany.social.linkedin}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <LinkedInIcon />
                  </IconButton>
                  <IconButton
                    href={`https://twitter.com/${mockCompany.social.twitter}`}
                    target="_blank"
                    rel="noopener"
                  >
                    <TwitterIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default CompanyViewPage; 